import React from "react";
import { Download, Eye, FileText, Video } from "lucide-react";
import useMaterialViewer, { isViewableType } from "../../Materials/useMaterialViewer";
import { canDownloadFile } from "../../ContentProtection/contentAccess";

/**
 * What a student can open from a syllabus day or topic: its materials and its
 * recordings. Shared by the day's "For the whole day" strip and each topic row.
 */

/** Material chips — a viewable (or view-only) file opens in the viewer, anything else downloads. */
export function MaterialChips({ materials = [] }) {
  const { openMaterial, downloadMaterial, viewer } = useMaterialViewer();
  if (!materials.length) return null;
  return (
    <>
      {materials.map((mat) => {
        // A view-only file always opens in the viewer, never as a download.
        const viewable = isViewableType(mat.fileType) || !canDownloadFile(mat);
        return (
          <button
            type="button"
            key={mat._id}
            className="ss-material-chip"
            onClick={() => (viewable ? openMaterial(mat) : downloadMaterial(mat))}
            title={`${viewable ? "View" : "Download"} ${mat.title}`}
          >
            <FileText size={11} aria-hidden="true" />
            <span>{mat.title}</span>
            {mat.fileType && <span className="ss-material-type">{mat.fileType}</span>}
            {viewable ? <Eye size={10} aria-hidden="true" /> : <Download size={10} aria-hidden="true" />}
          </button>
        );
      })}
      {viewer}
    </>
  );
}

/**
 * Recording links. A recording on the batch's Videos tab plays in the in-app
 * player (`onPlay`); a mapping from before that carried its own link opens it.
 */
export function RecordingLinks({ recordings = [], onPlay }) {
  return recordings.map((rec) => (rec.videoId ? (
    <button
      type="button"
      key={rec.videoId}
      className="ss-recording-link"
      onClick={() => onPlay?.(rec)}
      title={rec.title ? `Watch ${rec.title}` : "Watch the recording"}
    >
      <Video size={13} aria-hidden="true" />
      {rec.title || "Recording"}
    </button>
  ) : rec.recordingUrl && (
    <a
      key={rec.recordingId || rec.recordingUrl}
      href={rec.recordingUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="ss-recording-link"
    >
      <Video size={13} aria-hidden="true" />
      Recording
    </a>
  )));
}
